import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useEditorStore } from '../../store/editorStore';
import { useChatStore } from '../../store/chatStore';
import { useLLMStore } from '../../store/llmStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Simple non-streaming LLM call for similarity check
async function callLLMAPIOnce(prompt: string, config: any): Promise<string> {
  try {
    const response = await fetch(`${config.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: config.model,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 10, // Only need a number
        temperature: 0.1 // Low temperature for consistent output
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content?.trim() || '0';
  } catch (error) {
    console.error('LLM similarity check error:', error);
    return '0'; // Default to 0 on error
  }
}

const EXPERIMENT_SYSTEM_PROMPT = '你是一个专业的心理学和统计学专家，专门帮助学生理解心理与行为统计学的概念。请用清晰、易懂的语言回答问题，并提供相关的R代码示例。使用Markdown格式回复。';

/**
 * Streams a chat completion and reports the accumulated text as it arrives.
 * Updates are throttled so rendering Markdown does not run on every token.
 * Resolves with the full response text.
 */
async function streamExperimentReport(
  prompt: string,
  config: any,
  onProgress: (accumulatedText: string) => void
): Promise<string> {
  const response = await fetch(`${config.baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`
    },
    body: JSON.stringify({
      model: config.model,
      messages: [
        { role: 'system', content: EXPERIMENT_SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      max_tokens: 5000,
      temperature: 0.7,
      stream: true
    })
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let fullText = '';
  let lastFlush = 0;

  const flush = (force: boolean) => {
    const now = Date.now();
    if (force || now - lastFlush >= 60) {
      lastFlush = now;
      onProgress(fullText);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep the incomplete line for the next chunk

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (trimmedLine === 'data: [DONE]') {
          flush(true);
          return fullText;
        }

        if (trimmedLine.startsWith('data: ')) {
          try {
            const data = JSON.parse(trimmedLine.slice(6));
            const delta = data.choices?.[0]?.delta;
            if (delta?.content) {
              fullText += delta.content;
              flush(false);
            }
          } catch (e) {
            // Ignore keep-alive frames and partial JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  flush(true);
  return fullText;
}

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.98);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  max-width: 900px;
  width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(102, 126, 234, 0.1);
  border-radius: 20px 20px 0 0;
`;

const ModalTitle = styled.h2`
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:before {
    content: '🔬';
    font-size: 1.8rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(248, 113, 113, 0.3);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(85vh - 150px);
`;

const MethodCategories = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const CategoryButton = styled.button<{ $active: boolean }>`
  padding: 0.6rem 1.2rem;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MethodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const MethodCard = styled.button<{ $selected: boolean }>`
  padding: 1.2rem;
  background: ${props => props.$selected
    ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.$selected
    ? 'rgba(240, 147, 251, 0.8)'
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 15px;
  color: white;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    border-color: rgba(102, 126, 234, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const MethodName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #ffd700;
`;

const MethodDescription = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const MethodFunction = styled.div`
  font-size: 0.8rem;
  color: rgba(102, 126, 234, 255);
  background: rgba(102, 126, 234, 0.2);
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  align-self: flex-start;
`;

const ActionSection = styled.div`
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0 0 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SelectedMethodInfo = styled.div`
  background: rgba(102, 126, 234, 0.1);
  border-left: 4px solid #667eea;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const InfoTitle = styled.div`
  color: #ffd700;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const InfoDescription = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const InfoFunction = styled.div`
  color: rgba(102, 126, 234, 255);
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 15px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Custom Method Input Styles
const CustomMethodSection = styled.div`
  padding: 1.5rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 15px;
  margin-bottom: 1.5rem;
  border: 2px dashed rgba(102, 126, 234, 0.3);
`;

const CustomMethodTitle = styled.h3`
  color: #ffd700;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:before {
    content: '✏️';
  }
`;

const CustomMethodInput = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const CustomMethodHint = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  margin-top: 0.75rem;
  line-height: 1.5;
`;

const GenerateCustomButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: none;
  border-radius: 15px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(240, 147, 251, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid white;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

type MethodCategory = 'descriptive' | 'parametric' | 'correlationRegression' | 'nonparametric';

interface StatisticalMethod {
  id: string;
  name: string;
  description: string;
  category: MethodCategory;
  rFunction: string;
}

const STATISTICAL_METHODS: StatisticalMethod[] = [
  // Descriptive Statistics
  {
    id: 'mean',
    name: '均值 (Mean)',
    description: '计算数据的集中趋势',
    category: 'descriptive',
    rFunction: 'mean()'
  },
  {
    id: 'median',
    name: '中位数 (Median)',
    description: '数据的中间值',
    category: 'descriptive',
    rFunction: 'median()'
  },
  {
    id: 'mode',
    name: '众数 (Mode)',
    description: '出现最频繁的值',
    category: 'descriptive',
    rFunction: '自定义函数'
  },
  {
    id: 'sd',
    name: '标准差 (SD)',
    description: '衡量数据离散程度',
    category: 'descriptive',
    rFunction: 'sd()'
  },
  {
    id: 'variance',
    name: '方差 (Variance)',
    description: '数据偏离均值的程度',
    category: 'descriptive',
    rFunction: 'var()'
  },
  {
    id: 'quartiles',
    name: '四分位数 (Quartiles)',
    description: '数据分布的百分位数',
    category: 'descriptive',
    rFunction: 'quantile()'
  },
  {
    id: 'skewness',
    name: '偏度 (Skewness)',
    description: '分布的对称性',
    category: 'descriptive',
    rFunction: 'moments包'
  },
  {
    id: 'kurtosis',
    name: '峰度 (Kurtosis)',
    description: '分布的尖峭程度',
    category: 'descriptive',
    rFunction: 'moments包'
  },

  // Inferential Statistics
  {
    id: 'one-sample-t',
    name: '单样本t检验',
    description: '比较样本均值与已知值',
    category: 'parametric',
    rFunction: 't.test()'
  },
  {
    id: 'independent-t',
    name: '独立样本t检验',
    description: '比较两组独立样本均值',
    category: 'parametric',
    rFunction: 't.test(x, y)'
  },
  {
    id: 'paired-t',
    name: '配对样本t检验',
    description: '比较配对数据的差异',
    category: 'parametric',
    rFunction: 't.test(x, y, paired=TRUE)'
  },
  {
    id: 'one-sample-z',
    name: '单样本Z检验',
    description: '大样本下的均值检验',
    category: 'parametric',
    rFunction: 'BSDA包'
  },
  {
    id: 'binomial-test',
    name: '二项检验',
    description: '检验比例是否等于预期值',
    category: 'parametric',
    rFunction: 'binom.test()'
  },
  {
    id: 'chi-square-goodness',
    name: '卡方拟合优度检验',
    description: '检验观测分布与理论分布',
    category: 'parametric',
    rFunction: 'chisq.test()'
  },

  // Correlation
  {
    id: 'pearson',
    name: '皮尔逊相关',
    description: '衡量线性相关强度',
    category: 'correlationRegression',
    rFunction: 'cor.test(method="pearson")'
  },
  {
    id: 'spearman',
    name: '斯皮尔曼相关',
    description: '非参数等级相关',
    category: 'correlationRegression',
    rFunction: 'cor.test(method="spearman")'
  },
  {
    id: 'kendall',
    name: '肯德尔相关',
    description: '基于秩次的相关',
    category: 'correlationRegression',
    rFunction: 'cor.test(method="kendall")'
  },
  {
    id: 'partial-correlation',
    name: '偏相关',
    description: '控制其他变量的相关',
    category: 'correlationRegression',
    rFunction: 'ppcor包'
  },

  // Regression
  {
    id: 'simple-linear',
    name: '简单线性回归',
    description: '一个自变量的回归',
    category: 'correlationRegression',
    rFunction: 'lm(y ~ x)'
  },
  {
    id: 'multiple-linear',
    name: '多元线性回归',
    description: '多个自变量的回归',
    category: 'correlationRegression',
    rFunction: 'lm(y ~ x1 + x2 + ...)'
  },
  {
    id: 'logistic',
    name: '逻辑回归',
    description: '二分类因变量的回归',
    category: 'correlationRegression',
    rFunction: 'glm(y ~ x, family=binomial)'
  },
  {
    id: 'polynomial',
    name: '多项式回归',
    description: '非线性关系的拟合',
    category: 'correlationRegression',
    rFunction: 'lm(y ~ poly(x, n))'
  },

  // ANOVA
  {
    id: 'one-way-anova',
    name: '单因素方差分析',
    description: '比较三组及以上均值',
    category: 'parametric',
    rFunction: 'aov(y ~ group)'
  },
  {
    id: 'two-way-anova',
    name: '双因素方差分析',
    description: '分析两个因素的影响',
    category: 'parametric',
    rFunction: 'aov(y ~ factor1 * factor2)'
  },
  {
    id: 'repeated-anova',
    name: '重复测量方差分析',
    description: '重复测量的数据分析',
    category: 'parametric',
    rFunction: 'aov() + error()'
  },
  {
    id: 'ancova',
    name: '协方差分析',
    description: '控制协变量的方差分析',
    category: 'parametric',
    rFunction: 'aov(y ~ x + covariate)'
  },
  {
    id: 'mixed-anova',
    name: '混合方差分析',
    description: '组间因素与组内因素的混合设计',
    category: 'parametric',
    rFunction: 'ez包或afex包'
  },

  // Non-parametric
  {
    id: 'mann-whitney',
    name: 'Mann-Whitney U检验',
    description: '独立样本非参数检验',
    category: 'nonparametric',
    rFunction: 'wilcox.test()'
  },
  {
    id: 'wilcoxon',
    name: 'Wilcoxon符号秩检验',
    description: '配对样本非参数检验',
    category: 'nonparametric',
    rFunction: 'wilcox.test(paired=TRUE)'
  },
  {
    id: 'kruskal',
    name: 'Kruskal-Wallis检验',
    description: '多组非参数检验',
    category: 'nonparametric',
    rFunction: 'kruskal.test()'
  },
  {
    id: 'friedman',
    name: 'Friedman检验',
    description: '重复测量非参数检验',
    category: 'nonparametric',
    rFunction: 'friedman.test()'
  }
];

const CATEGORIES: { key: MethodCategory; label: string; icon: string }[] = [
  { key: 'descriptive', label: '描述统计', icon: '📊' },
  { key: 'parametric', label: '参数检验方法', icon: '🎯' },
  { key: 'correlationRegression', label: '相关与回归分析', icon: '🔗' },
  { key: 'nonparametric', label: '非参数检验方法', icon: '📉' }
];

interface MethodSelectorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCodeGenerated?: (code: string) => void;
  onReportReceived?: (report: string) => void;
  onStreamingChange?: (isStreaming: boolean) => void;
}

export const MethodSelectorPanel: React.FC<MethodSelectorPanelProps> = ({
  isOpen,
  onClose,
  onCodeGenerated,
  onReportReceived,
  onStreamingChange
}) => {
  const [activeCategory, setActiveCategory] = useState<MethodCategory>('descriptive');
  const [selectedMethod, setSelectedMethod] = useState<StatisticalMethod | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [customMethodInput, setCustomMethodInput] = useState('');
  const [isCustomMode, setIsCustomMode] = useState(false);

  const { setCode } = useEditorStore();
  const { sendMessage } = useChatStore();

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('%c=== [METHOD SELECTOR PANEL] COMPONENT MOUNTED ===', 'background: #2196f3; color: white; font-size: 14px; padding: 4px;');
    console.log('[METHOD SELECTOR] Available methods:', STATISTICAL_METHODS.length);
    const categories = Array.from(new Set(STATISTICAL_METHODS.map(m => m.category)));
    console.log('[METHOD SELECTOR] Categories:', categories);
  }, []);

  // Debug: Log when selected method changes
  useEffect(() => {
    console.log('%c=== [METHOD SELECTOR] Selected method changed ===', 'background: #9c27b0; color: white; padding: 4px;');
    console.log('[METHOD SELECTOR] Selected method:', selectedMethod?.name || 'None');
    console.log('[METHOD SELECTOR] Full object:', selectedMethod);
  }, [selectedMethod]);

  const filteredMethods = STATISTICAL_METHODS.filter(
    method => method.category === activeCategory
  );

  // Encourages varied, beginner-friendly R tooling instead of always defaulting to
  // ggplot2 plus base test functions. Every package listed is installable in webR.
  const buildToolkitGuidance = (includeInferential: boolean): string => {
    const lines = [
      'R工具选择（重要）：',
      '- 请不要默认总是使用ggplot2；请在不同案例中主动轮换下列不同工具，并优先选择**最容易学习**的实现方式',
      '- 可视化可选：',
      '  · base R（无需安装、最易上手）：hist()、boxplot()、plot()、barplot()、stripchart()、pairs()',
      '  · psych（专为心理学设计）：pairs.panels()、error.bars()、densityBy()',
      '  · 轻量专用图：vioplot::vioplot()、beeswarm::beeswarm()、corrplot::corrplot()、car::qqPlot()',
      '  · ggplot2 / ggpubr：仅当确实需要分组、分面或多图层叠加时才使用',
      '- 描述统计与心理测量可选：psych::describe()、psych::describeBy()、psych::alpha()、psych::corr.test()、DescTools、Hmisc'
    ];

    if (includeInferential) {
      lines.push(
        '- 推断统计可选：base R（t.test()、aov()、chisq.test()）、car::Anova()、car::leveneTest()、rstatix、afex 或 ez（重复测量）、coin 或 WRS2（非参数）、emmeans（事后比较）',
        '- 效应量可选：effectsize、lsr::cohensD()、DescTools'
      );
    } else {
      lines.push('- 若使用 psych::describe() 等一次输出多个指标的函数，请只解读与本主题统计量相关的部分');
    }

    lines.push(
      '- 核心统计量或检验请仍使用本主题指定的R函数，上述轮换主要用于可视化、描述统计与辅助分析',
      '- 请在注释中用一句话说明选择该工具的理由（例如更易学、或更贴近心理学研究习惯）',
      '- 使用非base包前必须先 webr::install("包名")，再 library(包名)'
    );

    return lines.join('\n');
  };

  const generateExperimentPrompt = (method: StatisticalMethod): string => {
    // Descriptive statistics only need computation + visualization, not the full
    // inferential workflow (hypotheses, test statistics, p-values, effect sizes).
    if (method.category === 'descriptive') {
      return `作为一个专业的心理学和统计学专家，请为"${method.name}"生成一个简洁的描述统计案例。

要求：
1. **研究场景**：用2-3句话简要说明一个真实的心理学研究背景和数据来源（不需要研究假设）
2. **数据模拟**：使用R代码生成一组合理的模拟数据（样本量合理，符合该统计量的适用条件）
3. **统计计算**：使用${method.rFunction}计算该统计量，只保留为解读该统计量所必需的辅助计算
4. **可视化**：提供与该统计量匹配的图形（如直方图、密度图、箱线图或条形图），并在图中标注该统计量
5. **结果解读**：用简短文字说明该数值的含义及其心理学意义

严格限制（非常重要）：
- 这是描述统计，**不要**包含任何推断统计内容
- **不要**提出H0/H1，**不要**进行假设检验
- **不要**输出p值、检验统计量、效应量或置信区间
- **不要**加入与"${method.name}"无关的其他统计分析
- 保持精简：R代码聚焦于"生成数据 → 计算统计量 → 可视化"三步

${buildToolkitGuidance(false)}

格式要求：
- 使用Markdown格式
- R代码使用\`\`\`r代码块标记
- 请提醒用户安装所需R包时，使用webr::install()函数
- 每个代码步骤前有#注释说明`;
    }

    return `作为一个专业的心理学和统计学专家，请为"${method.name}"生成一个完整的心理学实验案例。

要求：
1. **实验背景**：设计一个真实、有意义的心理学研究场景，详细说明研究目的和假设
2. **实验设计**：详细说明实验设计类型、被试信息、自变量和因变量
3. **数据模拟**：使用R代码生成符合该统计方法要求的模拟数据（样本量合理，数据符合方法假设）
4. **完整分析**：提供使用${method.rFunction}进行数据分析的完整R代码，每个步骤都有详细中文注释
5. **结果解释**：详细解释每个步骤的含义、输出结果的统计学意义和心理学意义

请提供：
- 清晰的实验背景和假设（H0和H1）
- 详细的中文注释说明每个R代码步骤
- 可以直接运行的R代码（包括数据生成、描述统计、推断检验、可视化）
- 对统计结果的完整解释（包括检验统计量、p值、效应量、置信区间）
- 假设检验的完整过程和结论
- 预期的心理学结果和解释

${buildToolkitGuidance(true)}

格式要求：
- 使用Markdown格式
- R代码使用\`\`\`r代码块标记
- 请提醒用户安装所需R包时，使用webr::install()函数
- 每个代码步骤前有#注释说明
- 结果解释部分包含统计学和心理学两层含义`;
  };

  // Generate prompt for custom method input by user
  const generateCustomMethodPrompt = (customInput: string): string => {
    return `作为一个专业的心理学和统计学专家，请根据用户提供的统计方法需求生成一个完整的心理学实验案例。

用户需求：
${customInput}

要求：
1. **理解用户需求**：准确理解用户想要使用的统计方法及其应用场景
2. **实验背景**：基于用户需求设计一个真实、有意义的心理学研究场景
3. **实验设计**：详细说明实验设计类型、被试信息、自变量和因变量
4. **数据模拟**：使用R代码生成符合该统计方法要求的模拟数据
5. **完整分析**：提供完整的R代码实现，每个步骤都有详细中文注释
6. **结果解释**：详细解释统计结果的统计学意义和心理学意义

自适应深度（非常重要）：
- 若用户需求属于**描述统计**（如均值、中位数、众数、标准差、方差、分位数、偏度、峰度等）：只需"数据模拟 → 统计量计算 → 可视化 → 简要解读"，**不要**提出H0/H1，**不要**做假设检验，**不要**输出p值、检验统计量、效应量或置信区间
- 若用户需求属于**推断统计**（如t检验、方差分析、回归、相关、非参数检验等）：提供完整流程，包括H0/H1、前提假设检验、检验统计量、p值、效应量、置信区间和最终结论
- 只输出与用户需求直接相关的分析，不要添加无关内容

请提供：
- 清晰的实验背景和假设
- 详细的中文注释说明每个R代码步骤
- 可以直接运行的R代码（包括数据生成、统计分析、可视化）
- 对统计结果的完整解释
- 请提醒用户安装所需R包时，使用webr::install()函数

${buildToolkitGuidance(true)}

格式要求：
- 使用Markdown格式
- R代码使用\`\`\`r代码块标记
- 每个代码步骤前有#注释说明`;
  };

  // Function to extract R code from markdown
  const extractRCode = (text: string): string => {
    // Extract all R code blocks from markdown
    const codeBlockRegex = /```r\n([\s\S]*?)```/g;
    const matches: string[] = [];
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      matches.push(match[1]);
    }

    if (matches.length > 0) {
      // Combine all R code blocks
      return matches.join('\n\n');
    }

    // If no code blocks found, try to extract any lines that look like R code
    const lines = text.split('\n');
    const codeLines: string[] = [];
    let inCodeSection = false;

    for (const line of lines) {
      // Skip markdown headers and bullet points
      if (line.match(/^#{1,6}\s/) || line.match(/^\s*[-*+]\s/) || line.match(/^\s*\d+\.\s/)) {
        continue;
      }

      // Include lines that look like R code or comments
      if (line.trim().startsWith('#') || line.includes('<-') || line.includes('=')) {
        inCodeSection = true;
      }

      if (inCodeSection) {
        codeLines.push(line);
      }

      // Stop if we hit a new markdown section
      if (inCodeSection && line.match(/^#{1,6}\s/)) {
        inCodeSection = false;
      }
    }

    return codeLines.join('\n');
  };

  // Function to check existing reports count
  const checkExistingReports = async (methodName: string, customDescription?: string): Promise<{ count: number; hasEnough: boolean }> => {
    try {
      let token = null;
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.token || null;
        } catch (e) {
          // Ignore parse errors
        }
      }

      let url = `${API_BASE_URL}/api/experiment-reports/reports/check/${encodeURIComponent(methodName)}`;
      if (customDescription) {
        url += `?customDescription=${encodeURIComponent(customDescription)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[CHECK EXISTING] Response:', result);
        // The API returns { success: true, count, hasEnough } directly, not wrapped in data
        return { count: result.count || 0, hasEnough: result.hasEnough || false };
      }
      return { count: 0, hasEnough: false };
    } catch (error) {
      console.error('Error checking existing reports:', error);
      return { count: 0, hasEnough: false };
    }
  };

  // Function to get a random existing report
  const getRandomExistingReport = async (methodName: string, customDescription?: string) => {
    try {
      let token = null;
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.token || null;
        } catch (e) {
          // Ignore parse errors
        }
      }

      let url = `${API_BASE_URL}/api/experiment-reports/reports/random/${encodeURIComponent(methodName)}`;
      if (customDescription) {
        url += `?customDescription=${encodeURIComponent(customDescription)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting random report:', error);
      return null;
    }
  };

  // Function to calculate similarity between two texts using LLM (supports all languages)
  const calculateSimilarity = async (text1: string, text2: string): Promise<number> => {
    try {
      console.log('[SIMILARITY] Starting LLM-based similarity check...');

      // Get LLM config
      const llmStore = useLLMStore.getState();
      console.log('[SIMILARITY] Loading LLM config...');
      await llmStore.loadConfig();

      console.log('[SIMILARITY] LLM config loaded:', {
        hasConfig: !!llmStore.config,
        provider: llmStore.config?.provider,
        name: llmStore.config?.name,
        hasApiKey: !!llmStore.config?.apiKey,
        apiKeyPrefix: llmStore.config?.apiKey?.substring(0, 10) + '...'
      });

      let config = llmStore.config;
      if (!config || !config.apiKey) {
        console.warn('[SIMILARITY] No LLM config found, falling back to basic similarity check');
        return calculateBasicSimilarity(text1, text2);
      }

      console.log('[SIMILARITY] Using LLM:', config.provider, config.model);

      // Extract the research scenario/title from each report (first 500 chars should contain the scenario description)
      const extractScenario = (text: string) => {
        // Get the first part which typically contains the experiment background/scenario
        const lines = text.split('\n').filter(line => line.trim().length > 0);
        // Take first 15 non-empty lines (should cover the experiment background and variables)
        return lines.slice(0, 15).join('\n');
      };

      const scenario1 = extractScenario(text1);
      const scenario2 = extractScenario(text2);

      console.log('[SIMILARITY] Extracted scenarios for comparison');
      console.log('[SIMILARITY] Scenario 1 length:', scenario1.length);
      console.log('[SIMILARITY] Scenario 2 length:', scenario2.length);

      const prompt = `You are analyzing whether two psychology experiment reports have the same research scenario.

Your task: Check if BOTH the independent variable (自变量) AND dependent variable (因变量) are the same.

Report 1 Scenario:
${scenario1}

Report 2 Scenario:
${scenario2}

Rules:
- Return HIGH similarity (0.8-1.0) ONLY if BOTH independent AND dependent variables are the same
- Return LOW similarity (0.0-0.3) if EITHER independent OR dependent variable is different
- We want diversity in scenarios, so be lenient - allow reports with different variables

Return ONLY a number from 0.0 to 1.0. Nothing else.`;

      console.log('[SIMILARITY] Calling LLM API...');
      const response = await callLLMAPIOnce(prompt, config);
      console.log('[SIMILARITY] LLM response:', response);

      // Parse the response to get a number
      const similarityMatch = response.match(/[\d.]+/);
      if (similarityMatch) {
        const similarity = parseFloat(similarityMatch[0]);
        // Ensure it's between 0 and 1
        const finalSimilarity = Math.max(0, Math.min(1, similarity));
        console.log('[SIMILARITY] Parsed similarity:', finalSimilarity);
        return finalSimilarity;
      }

      console.warn('[SIMILARITY] Could not parse similarity from LLM response, returning 0');
      return 0;
    } catch (error) {
      console.error('[SIMILARITY] LLM similarity check failed, falling back to basic:', error);
      return calculateBasicSimilarity(text1, text2);
    }
  };

  // Fallback basic similarity check (character-based for Chinese)
  const calculateBasicSimilarity = (text1: string, text2: string): number => {
    // For Chinese, use character-based comparison
    const chars1 = new Set(text1.split(''));
    const chars2 = new Set(text2.split(''));

    let intersectionCount = 0;
    chars1.forEach(char => {
      if (chars2.has(char)) {
        intersectionCount++;
      }
    });

    const unionCount = chars1.size + chars2.size - intersectionCount;
    return unionCount > 0 ? intersectionCount / unionCount : 0;
  };

  // Function to get existing reports for comparison
  const getExistingReportsForComparison = async (methodName: string, customDescription?: string) => {
    try {
      let token = null;
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.token || null;
        } catch (e) {
          // Ignore parse errors
        }
      }

      let url = `${API_BASE_URL}/api/experiment-reports/reports?limit=100`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Filter reports by the same method
        return result.data.filter((report: any) => report.statisticalMethod === methodName);
      }
      return [];
    } catch (error) {
      console.error('Error getting reports for comparison:', error);
      return [];
    }
  };

  // Function to save experiment report to backend
  const saveExperimentReport = async (
    statisticalMethod: string,
    methodCategory: string,
    isCustomMethod: boolean,
    customMethodDescription: string,
    rFunction: string,
    reportContent: string,
    rCode: string,
    llmProvider: string,
    llmModel: string
  ) => {
    console.log('%c=== [EXPERIMENT REPORT SAVE] Starting save process ===', 'background: #4caf50; color: white; padding: 4px;');
    console.log('[EXPERIMENT REPORT] Method:', statisticalMethod);
    console.log('[EXPERIMENT REPORT] Category:', methodCategory);
    console.log('[EXPERIMENT REPORT] Is Custom:', isCustomMethod);
    console.log('[EXPERIMENT REPORT] LLM Provider:', llmProvider);
    console.log('[EXPERIMENT REPORT] LLM Model:', llmModel);
    console.log('[EXPERIMENT REPORT] Report content length:', reportContent?.length || 0);
    console.log('[EXPERIMENT REPORT] R code length:', rCode?.length || 0);

    try {
      // Get existing reports for comparison to ensure diversity
      console.log('%c[EXPERIMENT REPORT] Checking for similar existing reports...', 'background: #ff9800; color: white; padding: 4px;');
      const existingReports = await getExistingReportsForComparison(statisticalMethod);
      console.log('%c[EXPERIMENT REPORT] Found ' + existingReports.length + ' existing reports for comparison', 'background: #2196f3; color: white; padding: 4px;');

      if (existingReports.length === 0) {
        console.log('[EXPERIMENT REPORT] No existing reports found, skipping similarity check and saving directly');
      } else {
        // Calculate similarity with each existing report
        const SIMILARITY_THRESHOLD = 0.5; // 50% similarity threshold
        let shouldSave = true;

        console.log('[EXPERIMENT REPORT] Starting similarity comparison loop...');
        for (const existingReport of existingReports) {
          console.log('[EXPERIMENT REPORT] Comparing with report:', existingReport._id);
          const similarity = await calculateSimilarity(reportContent, existingReport.reportContent);
          console.log('[EXPERIMENT REPORT] Similarity with report', existingReport._id, ':', (similarity * 100).toFixed(2) + '%');

          if (similarity >= SIMILARITY_THRESHOLD) {
            console.warn('%c[EXPERIMENT REPORT] Report too similar to existing report, skipping save', 'background: #f44336; color: white; padding: 4px;');
            shouldSave = false;
            break;
          }
        }

        if (!shouldSave) {
          console.log('=== [EXPERIMENT REPORT SAVE] SKIPPED (too similar to existing) ===');
          return;
        }
      }

      // Get token from auth-storage
      let token = null;
      const authStorage = localStorage.getItem('auth-storage');
      console.log('[EXPERIMENT REPORT] Auth storage exists:', !!authStorage);

      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.token || null;
          console.log('[EXPERIMENT REPORT] Token found:', !!token);
          console.log('[EXPERIMENT REPORT] Token length:', token?.length || 0);
          console.log('[EXPERIMENT REPORT] Token preview:', token ? token.substring(0, 30) + '...' : 'none');
        } catch (e) {
          console.log('[EXPERIMENT REPORT] Failed to parse auth-storage:', e);
        }
      }

      const payload = {
        statisticalMethod,
        methodCategory,
        isCustomMethod,
        customMethodDescription,
        rFunction,
        reportContent,
        rCode,
        llmProvider,
        llmModel,
        sessionId: localStorage.getItem('sessionId') || null
      };

      console.log('[EXPERIMENT REPORT] Payload prepared');
      console.log('[EXPERIMENT REPORT] Sending request to: ${API_BASE_URL}/api/experiment-reports/reports');

      const response = await fetch(`${API_BASE_URL}/api/experiment-reports/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      console.log('[EXPERIMENT REPORT] Response status:', response.status);
      console.log('[EXPERIMENT REPORT] Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('[EXPERIMENT REPORT] Saved successfully!');
        console.log('[EXPERIMENT REPORT] Report ID:', result.data?._id);
        console.log('[EXPERIMENT REPORT] User:', result.data?.user);
        console.log('=== [EXPERIMENT REPORT SAVE] SUCCESS ===');
      } else {
        const errorText = await response.text();
        console.warn('[EXPERIMENT REPORT] Failed to save');
        console.warn('[EXPERIMENT REPORT] Status:', response.status);
        console.warn('[EXPERIMENT REPORT] Error:', errorText);
        console.log('=== [EXPERIMENT REPORT SAVE] FAILED ===');
      }
    } catch (error) {
      console.error('[EXPERIMENT REPORT] Error saving:', error);
      console.error('[EXPERIMENT REPORT] Error details:', error instanceof Error ? error.message : error);
      console.log('=== [EXPERIMENT REPORT SAVE] ERROR ===');
    }
  };

  const handleGenerateExperiment = async () => {
    console.log('%c=== [HANDLE GENERATE] BUTTON CLICKED ===', 'background: #ff6b6b; color: white; font-size: 14px; padding: 4px;');
    console.log('[HANDLE GENERATE] Selected method:', selectedMethod);
    console.log('[HANDLE GENERATE] Selected method type:', typeof selectedMethod);
    console.log('[HANDLE GENERATE] Selected method keys:', selectedMethod ? Object.keys(selectedMethod) : 'N/A');
    console.log('[HANDLE GENERATE] Is generating:', isGenerating);

    if (!selectedMethod || isGenerating) {
      console.warn('%c[HANDLE GENERATE] EARLY RETURN - No method selected or already generating', 'background: #ffa500; color: white; padding: 4px;');
      console.warn('[HANDLE GENERATE] !selectedMethod:', !selectedMethod);
      console.warn('[HANDLE GENERATE] isGenerating:', isGenerating);
      return;
    }

    console.log('%c[HANDLE GENERATE] Starting generation process...', 'background: #4caf50; color: white; padding: 4px;');
    setIsGenerating(true);

    try {
      // Check if we have enough existing reports for this method
      console.log('[HANDLE GENERATE] Checking existing reports...');
      const { count, hasEnough } = await checkExistingReports(selectedMethod.name);
      console.log('[HANDLE GENERATE] Existing reports count:', count, 'Has enough:', hasEnough);

      if (hasEnough) {
        console.log('[HANDLE GENERATE] Using existing report from database');
        // Get a random existing report
        const existingReport = await getRandomExistingReport(selectedMethod.name);

        if (existingReport) {
          console.log('[HANDLE GENERATE] Found existing report, using it');
          const extractedCode = existingReport.rCode || extractRCode(existingReport.reportContent);

          if (extractedCode && extractedCode.trim().length > 0) {
            const finalCode = `# ===============================================
# ${selectedMethod.name} - 心理学实验案例
# ===============================================

${extractedCode}

# ===============================================
# 实验完成！
# 查看下方报告面板了解详细的实验设计和结果解释
# ===============================================
`;
            setCode(finalCode);
            onCodeGenerated?.(finalCode);
          }

          // Send the existing report to parent
          onReportReceived?.(existingReport.reportContent);

          // Close the panel
          onClose();
          setIsGenerating(false);
          console.log('[HANDLE GENERATE] Used existing report, process completed');
          return;
        }
      }

      // If we don't have enough reports or couldn't get one, generate new
      const prompt = generateExperimentPrompt(selectedMethod);
      console.log('[HANDLE GENERATE] Prompt generated');

      // 先在编辑器中生成基础代码框架
      const isDescriptiveMethod = selectedMethod.category === 'descriptive';
      const initialCode = isDescriptiveMethod
        ? `# ===============================================
# ${selectedMethod.name} - 描述统计案例
# ===============================================
# 📚 研究场景
#
# 📊 数据说明
#
# ===============================================

# 正在通过AI助手生成代码...
# 请稍候，AI助手正在为您生成：
# 1. 简要的研究场景
# 2. 符合实际的模拟数据
# 3. 使用 ${selectedMethod.rFunction} 计算统计量
# 4. 对应的可视化图形
#
# 详情将显示在下方的报告面板中
# ===============================================
`
        : `# ===============================================
# ${selectedMethod.name} - 心理学实验案例（实验案例以认知心理学、学习心理、社会心理、行为研究为主）
# ===============================================
# 📚 实验背景和目的
#
# 🎯 研究假设
# H0 (原假设):
# H1 (备择假设):
#
# 📊 实验设计
#
# ===============================================

# 正在通过AI助手生成完整的实验代码...
# 请稍候，AI助手正在为您生成：
# 1. 详细的实验背景和研究假设
# 2. 符合实际的模拟数据
# 3. 完整的数据分析步骤
# 4. 统计结果的专业解释
#
# 实验详情将显示在下方的报告面板中
# ===============================================
`;
      setCode(initialCode);
      console.log('[HANDLE GENERATE] Initial code set');

      // Clear previous report and notify parent component to start loading
      onCodeGenerated?.('');
      onReportReceived?.('');
      onStreamingChange?.(true);
      console.log('[HANDLE GENERATE] Parent components notified');

      // 模拟AI响应（实际会通过chatStore的sendMessage处理）
      // 由于我们需要拦截AI的响应，我们直接调用API
      // Use the LLM store config (user's configured provider) instead of chatStore config
      const llmStore = useLLMStore.getState();
      await llmStore.loadConfig();

      console.log('[HANDLE GENERATE] LLM config loaded:', {
        hasConfig: !!llmStore.config,
        provider: llmStore.config?.provider,
        name: llmStore.config?.name,
        baseUrl: llmStore.config?.baseUrl,
        model: llmStore.config?.model,
        hasApiKey: !!llmStore.config?.apiKey
      });

      const config: any = llmStore.config || useChatStore.getState().config;
      console.log('[HANDLE GENERATE] Using config from:', llmStore.config ? 'LLM Store (user configured)' : 'Chat Store (fallback)');

      if (config.apiKey && config.apiKey !== 'YOUR_DEEPSEEK_API_KEY') {
        console.log(`[HANDLE GENERATE] Streaming from ${config.provider || 'LLM'} API (${config.baseUrl})...`);
        try {
          // Close the selector so the report panel is visible while text streams in
          onClose();

          const aiResponse = await streamExperimentReport(prompt, config, (partialText) => {
            onReportReceived?.(partialText);
          });
          console.log('[HANDLE GENERATE] Streaming complete, length:', aiResponse?.length || 0);

          // 提取R代码
          const extractedCode = extractRCode(aiResponse);
          console.log('[HANDLE GENERATE] R code extracted, length:', extractedCode?.length || 0);

          // 更新编辑器中的代码
          if (extractedCode && extractedCode.trim().length > 0) {
            const finalCode = `# ===============================================
# ${selectedMethod.name} - 心理学实验案例
# ===============================================

${extractedCode}

# ===============================================
# 实验完成！
# 查看下方报告面板了解详细的实验设计和结果解释
# ===============================================
`;
            setCode(finalCode);
            onCodeGenerated?.(finalCode);
          }

          // 发送报告给父组件
          onReportReceived?.(aiResponse);
          console.log('[HANDLE GENERATE] Report sent to parent component');

          // Save experiment report to database (in background, don't block UI)
          console.log('=== [PRE-SAVE] About to save experiment report ===');
          console.log('[PRE-SAVE] Method name:', selectedMethod.name);
          console.log('[PRE-SAVE] Method category:', selectedMethod.category);
          console.log('[PRE-SAVE] AI Response length:', aiResponse?.length || 0);
          console.log('[PRE-SAVE] Extracted code length:', extractedCode?.length || 0);
          console.log('[PRE-SAVE] Calling saveExperimentReport...');

          // Call saveExperimentReport in background (don't await - let it run in parallel)
          saveExperimentReport(
            selectedMethod.name,
            selectedMethod.category,
            false,
            '',
            selectedMethod.rFunction,
            aiResponse,
            extractedCode || '',
            config.provider || 'deepseek',
            config.model || 'deepseek-chat'
          ).catch(error => {
            // Log save errors but don't block the UI
            console.error('[PRE-SAVE] Failed to save experiment report:', error);
          });

          console.log('[HANDLE GENERATE] saveExperimentReport call completed (background)');

        } catch (error) {
          console.error('[HANDLE GENERATE] API call failed:', error);
          console.error('[HANDLE GENERATE] Error details:', error);
          // 如果API失败，使用sendMessage作为后备
          await sendMessage(prompt);
        }
      } else {
        // 没有配置API，使用默认的sendMessage
        await sendMessage(prompt);
      }

      // 关闭选择面板
      console.log('[HANDLE GENERATE] Closing modal panel');
      onClose();
      console.log('=== [HANDLE GENERATE] Process completed ===');
    } catch (error) {
      console.error('[HANDLE GENERATE] Failed to generate experiment:', error);
      console.error('[HANDLE GENERATE] Error details:', error);
    } finally {
      console.log('[HANDLE GENERATE] Finally block, setting isGenerating to false');
      setIsGenerating(false);
      onStreamingChange?.(false);
    }
  };

  const handleGenerateCustomExperiment = async () => {
    if (!customMethodInput.trim() || isGenerating) return;

    setIsGenerating(true);

    try {
      // Check if we have enough existing reports for this custom method
      console.log('[CUSTOM GENERATE] Checking existing custom reports...');
      const { count, hasEnough } = await checkExistingReports('自定义', customMethodInput);
      console.log('[CUSTOM GENERATE] Existing custom reports count:', count, 'Has enough:', hasEnough);

      if (hasEnough) {
        console.log('[CUSTOM GENERATE] Using existing custom report from database');
        // Get a random existing report for this custom description
        const existingReport = await getRandomExistingReport('自定义', customMethodInput);

        if (existingReport) {
          console.log('[CUSTOM GENERATE] Found existing custom report, using it');
          const extractedCode = existingReport.rCode || extractRCode(existingReport.reportContent);

          if (extractedCode && extractedCode.trim().length > 0) {
            const finalCode = `# ===============================================
# 自定义统计方法 - 心理学实验案例（实验案例以认知心理学、学习心理、社会心理、行为研究为主）
# ===============================================

${extractedCode}

# ===============================================
# 实验完成！
# 查看下方报告面板了解详细的实验设计和结果解释
# ===============================================
`;
            setCode(finalCode);
            onCodeGenerated?.(finalCode);
          }

          // Send the existing report to parent
          onReportReceived?.(existingReport.reportContent);

          // Close the panel and reset
          onClose();
          setCustomMethodInput('');
          setIsCustomMode(false);
          setIsGenerating(false);
          console.log('[CUSTOM GENERATE] Used existing report, process completed');
          return;
        }
      }

      // If we don't have enough reports or couldn't get one, generate new
      const prompt = generateCustomMethodPrompt(customMethodInput);

      // 先在编辑器中生成基础代码框架
      const initialCode = `# ===============================================
# 自定义统计方法 - 心理学实验案例
# ===============================================
# 📚 用户需求
# ${customMethodInput.substring(0, 100)}${customMethodInput.length > 100 ? '...' : ''}
#
# 🎯 正在生成实验案例...
# ===============================================

# 正在通过AI助手生成完整的实验代码...
# 请稍候，AI助手正在为您生成：
# 1. 详细的实验背景和研究假设
# 2. 符合实际的模拟数据
# 3. 完整的数据分析步骤
# 4. 统计结果的专业解释
#
# 实验详情将显示在下方的报告面板中
# ===============================================
`;
      setCode(initialCode);

      // Clear previous report and notify parent component to start loading
      onCodeGenerated?.('');
      onReportReceived?.('');
      onStreamingChange?.(true);

      // Load LLM config for custom method generation
      const llmStore = useLLMStore.getState();
      await llmStore.loadConfig();
      const config: any = llmStore.config || useChatStore.getState().config;

      if (config.apiKey && config.apiKey !== 'YOUR_DEEPSEEK_API_KEY') {
        try {
          // Close the selector so the report panel is visible while text streams in
          onClose();

          const aiResponse = await streamExperimentReport(prompt, config, (partialText) => {
            onReportReceived?.(partialText);
          });

          // 提取R代码
          const extractedCode = extractRCode(aiResponse);

          // 更新编辑器中的代码
          if (extractedCode && extractedCode.trim().length > 0) {
            const finalCode = `# ===============================================
# 自定义统计方法 - 心理学实验案例
# ===============================================

${extractedCode}

# ===============================================
# 实验完成！
# 查看下方报告面板了解详细的实验设计和结果解释
# ===============================================
`;
            setCode(finalCode);
            onCodeGenerated?.(finalCode);
          }

          // 发送报告给父组件
          onReportReceived?.(aiResponse);

          // Save custom experiment report to database
          console.log('=== [PRE-SAVE CUSTOM] About to save custom experiment report ===');
          console.log('[PRE-SAVE CUSTOM] Custom input length:', customMethodInput?.length || 0);
          console.log('[PRE-SAVE CUSTOM] AI Response length:', aiResponse?.length || 0);
          console.log('[PRE-SAVE CUSTOM] LLM Provider:', config.provider || 'deepseek');
          console.log('[PRE-SAVE CUSTOM] LLM Model:', config.model || 'deepseek-chat');
          saveExperimentReport(
            '自定义方法',
            'custom',
            true,
            customMethodInput,
            '自定义',
            aiResponse,
            extractedCode || '',
            config.provider || 'deepseek',
            config.model || 'deepseek-chat'
          );

        } catch (error) {
          console.error('API call failed:', error);
          await sendMessage(prompt);
        }
      } else {
        await sendMessage(prompt);
      }

      // 关闭选择面板并重置
      onClose();
      setCustomMethodInput('');
      setIsCustomMode(false);
    } catch (error) {
      console.error('Failed to generate custom experiment:', error);
    } finally {
      setIsGenerating(false);
      onStreamingChange?.(false);
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return createPortal(
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>统计方法实验室</ModalTitle>
          <CloseButton onClick={onClose} title="关闭 (ESC)">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <MethodCategories>
            {CATEGORIES.map(category => (
              <CategoryButton
                key={category.key}
                $active={activeCategory === category.key && !isCustomMode}
                onClick={() => {
                  setActiveCategory(category.key);
                  setSelectedMethod(null);
                  setIsCustomMode(false);
                }}
              >
                {category.icon} {category.label}
              </CategoryButton>
            ))}
            <CategoryButton
              $active={isCustomMode}
              onClick={() => {
                setIsCustomMode(true);
                setSelectedMethod(null);
              }}
            >
              ✏️ 自定义方法
            </CategoryButton>
          </MethodCategories>

          {isCustomMode ? (
            <CustomMethodSection>
              <CustomMethodTitle>输入自定义统计方法</CustomMethodTitle>
              <CustomMethodInput
                value={customMethodInput}
                onChange={(e) => setCustomMethodInput(e.target.value)}
                placeholder="请描述您想要使用的统计方法或实验需求...

示例：
• 我想用重复测量方差分析来比较前后测的差异
• 使用逻辑回归分析影响大学生学习动机的因素
• 用皮尔逊相关分析焦虑与抑郁程度的关系

请详细说明：
1. 您想使用的统计方法
2. 研究目的或假设
3. 变量类型（如果有）"
              />
              <CustomMethodHint>
                <i className="fas fa-lightbulb"></i> 提示：描述越详细，生成的实验案例越符合您的需求
              </CustomMethodHint>
            </CustomMethodSection>
          ) : (
            <MethodsGrid>
              {filteredMethods.map(method => (
                <MethodCard
                  key={method.id}
                  $selected={selectedMethod?.id === method.id}
                  onClick={() => {
                    console.log('%c=== [METHOD CARD CLICKED] ===', 'background: #00bcd4; color: white; padding: 4px;');
                    console.log('[METHOD CARD] Name:', method.name);
                    console.log('[METHOD CARD] ID:', method.id);
                    console.log('[METHOD CARD] Category:', method.category);
                    console.log('[METHOD CARD] Full object:', method);
                    setSelectedMethod(method);
                  }}
                >
                  <MethodName>{method.name}</MethodName>
                  <MethodDescription>{method.description}</MethodDescription>
                  <MethodFunction>{method.rFunction}</MethodFunction>
                </MethodCard>
              ))}
            </MethodsGrid>
          )}
        </ModalBody>

        <ActionSection>
          {isCustomMode ? (
            <>
              {customMethodInput.trim() && (
                <SelectedMethodInfo>
                  <InfoTitle>✏️ 您的自定义需求</InfoTitle>
                  <InfoDescription>{customMethodInput.substring(0, 200)}{customMethodInput.length > 200 ? '...' : ''}</InfoDescription>
                </SelectedMethodInfo>
              )}
              <GenerateCustomButton
                onClick={handleGenerateCustomExperiment}
                disabled={isGenerating || !customMethodInput.trim()}
              >
                {isGenerating ? (
                  <>
                    <LoadingSpinner />
                    正在生成自定义实验案例...
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic"></i>
                    生成自定义实验案例
                  </>
                )}
              </GenerateCustomButton>
            </>
          ) : selectedMethod ? (
            <>
              <SelectedMethodInfo>
                <InfoTitle>✨ 已选择：{selectedMethod.name}</InfoTitle>
                <InfoDescription>{selectedMethod.description}</InfoDescription>
                <InfoFunction>
                  <i className="fas fa-code"></i> R函数：{selectedMethod.rFunction}
                </InfoFunction>
              </SelectedMethodInfo>
              <GenerateButton
                onClick={handleGenerateExperiment}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <LoadingSpinner />
                    正在生成实验案例...
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic"></i>
                    {selectedMethod.category === 'descriptive' ? '生成描述统计案例' : '生成完整实验案例'}
                  </>
                )}
              </GenerateButton>
            </>
          ) : (
            <GenerateButton disabled>
              <i className="fas fa-hand-pointer"></i>
              请先选择一个统计方法或使用自定义方法
            </GenerateButton>
          )}
        </ActionSection>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
